'use memo';

import { useCallback, useEffect, useMemo, useState } from 'react';

import SectionCard from '@/components/Card/SectionCard';
import Icon from '@/components/Icon/Icon';
import { Loader } from '@/components/Loading';
import { ProgressStepBar } from '@/components/ProgressStepBar/ProgressStepBar';
import { Text } from '@/components/Typography/Text';
import { CFormSectionDetailLayout } from '@/features/c-form/CFormSectionDetailLayout';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useDeviceDetailFormStore } from '../../-shared';
import type {
  DeviceDetailSection,
  DeviceStatusResponse,
} from '../../-shared/-query/type';
import { DeviceDetailAttributeRenderer } from '../../-shared/AttributeRenderer';
import { ReadonlyAttributeRenderer } from '../../-shared/ReadonlyAttributeRenderer';

interface Props {
  currentStatusSections: DeviceDetailSection[];
  scheduleSections: DeviceDetailSection[];
  isPending: boolean;
  readOnly?: boolean;
  saveMutateAsync: (request: DeviceStatusResponse) => Promise<unknown>;
}

export function StatusTimeline({
  currentStatusSections,
  scheduleSections,
  isPending,
  readOnly = false,
  saveMutateAsync,
}: Props) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.device.status',
  });
  const { actions } = useDeviceDetailFormStore();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const currentStatusData = useMemo(
    (): DeviceStatusResponse => ({
      sections: currentStatusSections,
    }),
    [currentStatusSections],
  );

  useEffect(() => {
    actions.reset(currentStatusData);
  }, [actions, currentStatusData]);

  const handleSave = useCallback(async () => {
    const currentSectionKey =
      currentStatusSections[0]?.sectionKey !== undefined
        ? String(currentStatusSections[0].sectionKey)
        : undefined;
    if (!actions.validate(currentSectionKey)) {
      throw new Error('Validation failed');
    }
    await saveMutateAsync(actions.getUpdateRequest());
  }, [actions, saveMutateAsync, currentStatusSections]);

  const handleReset = useCallback(() => {
    actions.reset(currentStatusData);
  }, [actions, currentStatusData]);

  const stepLabels = useMemo(() => {
    const labels = [t('current')];
    for (const section of scheduleSections) {
      labels.push(section.name ?? String(section.sectionKey));
    }
    return labels;
  }, [t, scheduleSections]);

  const selectedSection = useMemo(() => {
    if (selectedIndex === 0) {
      return currentStatusSections[0];
    }
    return scheduleSections[selectedIndex - 1];
  }, [selectedIndex, currentStatusSections, scheduleSections]);

  const renderEditableSectionContent = useCallback(
    (section: DeviceDetailSection) => (
      <DeviceDetailAttributeRenderer
        attributes={section.attributes}
        sectionKey={String(section.sectionKey)}
        displayOrder={section.displayOrder}
      />
    ),
    [],
  );

  const renderReadonlySectionContent = useCallback(
    (section: DeviceDetailSection) => (
      <ReadonlyAttributeRenderer
        attributes={section.attributes}
        sectionKey={String(section.sectionKey)}
      />
    ),
    [],
  );

  if (isPending) {
    return <Loader />;
  }

  const isCurrentStep = selectedIndex === 0;
  const canEditCurrentStep = isCurrentStep && !readOnly;

  return (
    <div>
      <div className="flex flex-col gap-2 px-12 pt-3 pb-3 border-b border-neutral-muted-border max-md:px-6">
        <div className="flex items-center gap-2">
          <Icon iconName="SettingsIcon" className="h-5 w-5" />
          <Text variant="h4">{t('timeline')}</Text>
        </div>
        <ProgressStepBar
          prefixVariant="icon"
          stepLabels={stepLabels}
          currentStepIndex={selectedIndex}
          onStepClick={setSelectedIndex}
        />
      </div>

      <div className="px-12 py-6 max-md:p-6" role="tabpanel">
        {selectedSection &&
          (canEditCurrentStep ? (
            <CFormSectionDetailLayout
              sections={currentStatusSections}
              isLoading={false}
              error={null}
              linkTo="."
              itemLayout="card"
              showNavigation={false}
              onSave={handleSave}
              onReset={handleReset}
              renderSectionContent={renderEditableSectionContent}
            />
          ) : (
            <SectionCard
              id={`status-section-${selectedSection.displayOrder}`}
              title={
                <Text variant="h4">
                  {selectedSection.name ?? String(selectedSection.sectionKey)}
                </Text>
              }
            >
              {renderReadonlySectionContent(selectedSection)}
            </SectionCard>
          ))}
      </div>
    </div>
  );
}
